import React from "react";
import { CaretDown } from "@phosphor-icons/react";
import { useLang } from "@/contexts/LangContext";
import { displayName } from "@/lib/bibleData";

// Detect where the New Testament begins. Both the ES (Jerusalén) and EN
// (CPDV) data files use the conventional Catholic ordering, so the first
// book of the NT is always Matthew/Mateo (regardless of how the file
// stores accents or leading spaces — `displayName`/`.trim()` upstream).
function findNTStartIndex(books) {
    return books.findIndex((b) => /^(matthew|mateo)$/i.test((b.name || "").trim()));
}

/**
 * BibleAccordionIndex — vertical full-width list of all books in the
 * loaded Bible. Tapping a book card expands it inline to reveal a 5-col
 * grid of chapter numbers; tapping a chapter triggers `onPick(bookIdx,
 * chapterNumber)`.
 *
 * Performance notes
 * -----------------
 * The list has up to 73 entries (and a couple of those — Psalms, Isaiah —
 * each pop a 150-button grid when expanded). To keep scroll buttery on
 * mobile we lean on `content-visibility: auto` so off-screen cards skip
 * layout/paint entirely, and we render the chapter grid only for the
 * currently expanded book so React never holds 1,189 DOM nodes at once.
 *
 * Accordion model: at most one book is expanded at a time. Re-tapping
 * the open card collapses it. After expanding, the card scrolls itself
 * into view so the chapter grid is never hidden below the fold.
 */
export default function BibleAccordionIndex({ books, onPick, activeBookIdx = -1 }) {
    const { lang } = useLang();
    const [openIdx, setOpenIdx] = React.useState(activeBookIdx >= 0 ? activeBookIdx : -1);
    const cardRefs = React.useRef([]);

    // Locate the OT/NT boundary once. -1 means we couldn't classify the
    // canon (e.g. a partial translation) — in that case we simply don't
    // render the second separator, preserving graceful degradation.
    const ntStartIdx = React.useMemo(() => findNTStartIndex(books), [books]);

    const toggle = React.useCallback((i) => {
        setOpenIdx((cur) => (cur === i ? -1 : i));
        // Defer the scroll until the chapter grid has actually mounted so
        // the browser knows the card's new height.
        requestAnimationFrame(() => {
            const el = cardRefs.current[i];
            if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
    }, []);

    const otLabel = lang === "es" ? "Antiguo Testamento" : "Old Testament";
    const ntLabel = lang === "es" ? "Nuevo Testamento" : "New Testament";

    return (
        <ol
            className="flex flex-col gap-2"
            data-testid="bible-accordion"
            aria-label="Bible book index"
        >
            <TestamentSeparator label={otLabel} testId="bible-idx-sep-ot" />
            {books.map((b, i) => {
                const isOpen = openIdx === i;
                const isActive = activeBookIdx === i;
                return (
                    <React.Fragment key={b.name}>
                        {i === ntStartIdx && (
                            <TestamentSeparator label={ntLabel} testId="bible-idx-sep-nt" />
                        )}
                        <li
                            ref={(el) => { cardRefs.current[i] = el; }}
                            style={{ contentVisibility: "auto", containIntrinsicSize: "1px 72px" }}
                        >
                            <button
                                type="button"
                                onClick={() => toggle(i)}
                                aria-expanded={isOpen}
                                aria-controls={`bible-idx-chapters-${i}`}
                                data-testid={`bible-idx-book-${i}`}
                                className={[
                                    "w-full flex items-center justify-between gap-3",
                                    "px-4 py-3.5 rounded-md text-left transition-colors",
                                    "ui-sans select-none",
                                    isOpen
                                        ? "border-2 border-blue-600 bg-blue-50/60"
                                        : isActive
                                            ? "border border-blue-300 bg-blue-50/30"
                                            : "border border-sand-300 bg-white hover:border-blue-400",
                                ].join(" ")}
                            >
                                <span className="reading-serif text-base sm:text-lg leading-tight flex-1 min-w-0 truncate">
                                    {displayName(b.name, lang)}
                                </span>
                                <span className="ui-sans text-xs text-stoneMuted shrink-0 tabular-nums">
                                    {b.chapters.length} {lang === "es" ? "cap." : "ch."}
                                </span>
                                <CaretDown
                                    size={14}
                                    weight="bold"
                                    aria-hidden="true"
                                    className={[
                                        "shrink-0 text-stoneMuted transition-transform duration-200",
                                        isOpen ? "rotate-180" : "",
                                    ].join(" ")}
                                />
                            </button>
                            {isOpen && (
                                <div
                                    id={`bible-idx-chapters-${i}`}
                                    role="group"
                                    className="grid grid-cols-5 gap-1.5 mt-2 mb-1 px-1"
                                    data-testid={`bible-idx-chapters-${i}`}
                                >
                                    {b.chapters.map((c) => c.chapter).map((ch) => (
                                        <button
                                            key={ch}
                                            type="button"
                                            onClick={() => onPick(i, ch)}
                                            data-testid={`bible-idx-ch-${i}-${ch}`}
                                            className={[
                                                "aspect-square inline-flex items-center justify-center",
                                                "rounded-md border ui-sans text-sm tabular-nums",
                                                "transition-colors",
                                                "border-sand-300 bg-white text-stone900",
                                                "hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50",
                                                "active:scale-95",
                                            ].join(" ")}
                                        >
                                            {ch}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </li>
                    </React.Fragment>
                );
            })}
        </ol>
    );
}

/**
 * TestamentSeparator — sticky section label between OT and NT books.
 * Pinned just under the page-level sticky search bar so the user always
 * sees which testament they're scrolling through. Both separators share
 * the same top offset, so when the second one reaches it the first is
 * naturally pushed out — the canonical "stuck headers" iOS behaviour.
 */
function TestamentSeparator({ label, testId }) {
    return (
        <li
            role="separator"
            aria-label={label}
            data-testid={testId}
            // Page-level sticky search bar measures ~80 px tall (py-3 +
            // ~40 px input). It pins at top-56 (mobile) / top-72 (desktop),
            // so the bottom edges sit at 136 / 152 px respectively. We pin
            // a few extra pixels lower so the label visibly clears the
            // bar's translucent background instead of slipping under it.
            className="sticky top-[140px] md:top-[160px] z-10 -mx-1 px-1"
        >
            <div className="flex items-center gap-3 py-2 bg-sand-50/95 backdrop-blur-md">
                <span className="h-px flex-1 bg-sand-300" aria-hidden="true" />
                <span className="ui-sans uppercase tracking-[0.18em] text-[11px] font-semibold text-stoneMuted shrink-0">
                    {label}
                </span>
                <span className="h-px flex-1 bg-sand-300" aria-hidden="true" />
            </div>
        </li>
    );
}
